import { useState } from 'react';
import { Encabezado, Pestanas } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cajon,
  Campo,
  Cargando,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
  clases,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { actualizar, enviar } from '../api/cliente';
import { fecha, gs, legible } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import { BuscadorDePaciente } from './FichaPaciente';
import type {
  ConvenioVista,
  PacienteResumen,
  PlanTratamiento,
  Prestacion,
  Presupuesto,
  Tarifario,
} from '../api/tipos';

const FASES = ['Urgencia', 'Periodontal', 'Restauradora', 'Protesica', 'Mantenimiento'] as const;

/**
 * Tratamientos: el catálogo con sus precios, y el recorrido plan → presupuesto → aceptado.
 * El plan es lo que se le explica al paciente; el presupuesto es lo que firma.
 */
export function Tratamientos() {
  const [pestana, setPestana] = useState('planes');

  return (
    <>
      <Encabezado titulo="Tratamientos" detalle="Planes, presupuestos y catálogo de prestaciones." />

      <Pestanas
        actual={pestana}
        alCambiar={setPestana}
        opciones={[
          { valor: 'planes', rotulo: 'Planes y presupuestos' },
          { valor: 'catalogo', rotulo: 'Catálogo y precios' },
        ]}
      />

      {pestana === 'planes' ? <PlanesYPresupuestos /> : <Catalogo />}
    </>
  );
}

function Catalogo() {
  const tarifarios = useDatos<Tarifario[]>('/api/v1/tratamientos/tarifarios');
  const [tarifarioId, setTarifarioId] = useState('');

  const elegido = tarifarioId || tarifarios.datos?.[0]?.id || '';
  const prestaciones = useDatos<Prestacion[]>(
    elegido ? `/api/v1/tratamientos/prestaciones?tarifarioId=${elegido}` : null,
    [elegido],
  );

  return (
    <div className="space-y-4">
      <Panel titulo="Tarifarios" descripcion="El particular es el que se usa cuando no hay convenio.">
        {tarifarios.cargando && <Cargando que="los tarifarios" />}
        {tarifarios.datos && (
          <div className="flex flex-wrap gap-2">
            {tarifarios.datos.map((tarifario) => (
              <button
                key={tarifario.id}
                type="button"
                aria-pressed={elegido === tarifario.id}
                onClick={() => setTarifarioId(tarifario.id)}
                className={clases(
                  'min-h-tactil rounded-lg border px-3 text-left text-sm transition-colors',
                  elegido === tarifario.id
                    ? 'border-marca-600 bg-marca-50 text-marca-700'
                    : 'border-papel-300 bg-white text-tinta-600 hover:bg-papel-50',
                )}
              >
                <span className="block font-medium">{tarifario.nombre}</span>
                <span className="block text-dato opacity-80">
                  {legible(tarifario.tipo)} · {tarifario.precios} precios · desde{' '}
                  {fecha(tarifario.vigenteDesde)}
                </span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      <Panel titulo="Prestaciones">
        {prestaciones.cargando && <Cargando que="el catálogo" />}
        {prestaciones.error && <ErrorEnPantalla mensaje={prestaciones.error} />}
        {prestaciones.datos && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Código</th>
                  <th scope="col">Prestación</th>
                  <th scope="col">Fase</th>
                  <th scope="col">Exige</th>
                  <th scope="col" className="text-right">Precio</th>
                </tr>
              </thead>
              <tbody>
                {prestaciones.datos.map((prestacion) => (
                  <tr key={prestacion.id}>
                    <td className="cifra whitespace-nowrap font-medium">{prestacion.codigo}</td>
                    <td>{prestacion.nombre}</td>
                    <td>
                      <Insignia>{legible(prestacion.fase)}</Insignia>
                    </td>
                    <td className="text-dato text-tinta-500">
                      {[prestacion.requierePieza && 'pieza', prestacion.requiereSuperficie && 'superficie']
                        .filter(Boolean)
                        .join(' y ') || '—'}
                    </td>
                    <td className="cifra whitespace-nowrap text-right font-medium">
                      {gs(prestacion.precio)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>
    </div>
  );
}

function PlanesYPresupuestos() {
  const [paciente, setPaciente] = useState<PacienteResumen | null>(null);

  return (
    <div className="space-y-4">
      <Panel titulo="Elegí el paciente">
        <BuscadorDePaciente id="buscar-paciente-plan" elegidoId={paciente?.id} alElegir={setPaciente} />
      </Panel>

      {!paciente && (
        <Panel>
          <Vacio
            titulo="Buscá un paciente para ver sus planes"
            detalle="El plan agrupa lo que hay que hacer por fases; del plan sale el presupuesto."
          />
        </Panel>
      )}

      {paciente && <PlanesDelPaciente paciente={paciente} />}
    </div>
  );
}

function PlanesDelPaciente({ paciente }: { paciente: PacienteResumen }) {
  const { puede } = useSesion();
  const presupuestos = useDatos<Presupuesto[]>(
    `/api/v1/pacientes/${paciente.id}/presupuestos`,
    [paciente.id],
  );
  const planes = useDatos<PlanTratamiento[]>(`/api/v1/pacientes/${paciente.id}/planes`, [paciente.id]);
  const [creando, setCreando] = useState(false);
  const [presupuestando, setPresupuestando] = useState<PlanTratamiento | null>(null);
  const { correr, ejecutando, error } = useAccion();
  const [aviso, setAviso] = useState<string | null>(null);

  const aceptar = async (presupuesto: Presupuesto) => {
    const hecho = await correr(() =>
      actualizar(`/api/v1/presupuestos/${presupuesto.id}/estado`, { estado: 'Aceptado' }),
    );

    if (hecho !== null) {
      setAviso(`Presupuesto ${presupuesto.numero}/${presupuesto.version} aceptado y con precios congelados.`);
      presupuestos.recargar();
    }
  };

  const empezarItem = async (planId: string, itemId: string) => {
    const hecho = await correr(() => enviar(`/api/v1/tratamientos/planes/${planId}/items/${itemId}/empezar`, {}));

    if (hecho !== null) {
      planes.recargar();
    }
  };

  const ejecutarItem = async (planId: string, itemId: string) => {
    const hecho = await correr(() => enviar(`/api/v1/tratamientos/planes/${planId}/items/${itemId}/ejecutar`, {}));

    if (hecho !== null) {
      planes.recargar();
    }
  };

  return (
    <div className="space-y-4">
      {aviso && (
        <Aviso tono="exito" alCerrar={() => setAviso(null)}>
          {aviso}
        </Aviso>
      )}
      {error && <Aviso tono="alerta">{error}</Aviso>}

      <Panel
        titulo={`Planes de ${paciente.nombreCompleto}`}
        accion={
          puede('tratamiento.planificar') && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              Nuevo plan
            </Boton>
          )
        }
      >
        {planes.cargando && <Cargando que="los planes" />}
        {planes.datos?.length === 0 && (
          <Vacio
            titulo="Sin planes"
            detalle="Armá el primero con las prestaciones que el paciente necesita, ordenadas por fase."
            accion={
              puede('tratamiento.planificar') && (
                <Boton variante="primario" onClick={() => setCreando(true)}>
                  Nuevo plan
                </Boton>
              )
            }
          />
        )}

        {planes.datos && planes.datos.length > 0 && (
          <ul className="space-y-4">
            {planes.datos.map((plan) => (
              <li key={plan.id} className="rounded-lg border border-papel-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-tinta-900">{plan.nombre}</p>
                    <p className="text-dato text-tinta-500">
                      {legible(plan.estado)} · avance {plan.porcentajeAvance} %
                    </p>
                  </div>
                  {puede('presupuesto.crear') && plan.estado === 'Activo' && (
                    <Boton onClick={() => setPresupuestando(plan)}>Presupuestar</Boton>
                  )}
                </div>

                <div
                  role="progressbar"
                  aria-valuenow={plan.porcentajeAvance}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-papel-200"
                >
                  <div
                    className="h-full rounded-full bg-marca-500"
                    style={{ width: `${plan.porcentajeAvance}%` }}
                  />
                </div>

                <ul className="mt-3 space-y-1">
                  {plan.items.map((item) => (
                    <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 text-dato">
                      <span className="min-w-0 truncate text-tinta-700">
                        <Insignia className="mr-2">{legible(item.fase)}</Insignia>
                        {item.prestacion}
                        {item.pieza ? ` · pieza ${item.pieza}` : ''}
                      </span>
                      <span className="flex items-center gap-2">
                        <Insignia
                          tono={
                            item.estado === 'Ejecutado'
                              ? 'exito'
                              : item.estado === 'EnCurso'
                                ? 'aviso'
                                : item.estado === 'Anulado'
                                  ? 'alerta'
                                  : 'neutro'
                          }
                        >
                          {legible(item.estado)}
                        </Insignia>
                        {puede('tratamiento.planificar') && plan.estado === 'Activo' && (
                          <>
                            {item.estado === 'Pendiente' && (
                              <Boton disabled={ejecutando} onClick={() => void empezarItem(plan.id, item.id)}>
                                Empezar
                              </Boton>
                            )}
                            {(item.estado === 'Pendiente' || item.estado === 'EnCurso') && (
                              <Boton
                                variante="primario"
                                disabled={ejecutando}
                                onClick={() => void ejecutarItem(plan.id, item.id)}
                              >
                                Marcar ejecutado
                              </Boton>
                            )}
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel titulo="Presupuestos">
        {presupuestos.cargando && <Cargando que="los presupuestos" />}
        {presupuestos.datos?.length === 0 && (
          <Vacio titulo="Sin presupuestos" detalle="Se arman desde un plan activo." />
        )}

        {presupuestos.datos && presupuestos.datos.length > 0 && (
          <ul className="space-y-3">
            {presupuestos.datos.map((presupuesto) => (
              <li key={presupuesto.id} className="rounded-lg border border-papel-200 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="cifra text-sm font-semibold text-tinta-900">
                      N.º {presupuesto.numero}/{presupuesto.version}
                    </p>
                    <p className="text-dato text-tinta-500">
                      Vigente {fecha(presupuesto.vigenciaDesde)} – {fecha(presupuesto.vigenciaHasta)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Insignia tono={presupuesto.estado === 'Aceptado' ? 'exito' : 'neutro'}>
                      {legible(presupuesto.estado)}
                    </Insignia>
                    {puede('presupuesto.aceptar') && presupuesto.estado !== 'Aceptado' && (
                      <Boton
                        variante="primario"
                        disabled={ejecutando}
                        onClick={() => void aceptar(presupuesto)}
                      >
                        Aceptar
                      </Boton>
                    )}
                  </div>
                </div>

                <Desplazable>
                  <table className="tabla mt-3">
                    <thead>
                      <tr>
                        <th scope="col">Prestación</th>
                        <th scope="col" className="text-right">Precio</th>
                        <th scope="col" className="text-right">Convenio</th>
                        <th scope="col" className="text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {presupuesto.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.prestacion}
                            {item.pieza ? ` · ${item.pieza}` : ''}
                          </td>
                          <td className="cifra text-right">{gs(item.precioUnitario)}</td>
                          <td className="cifra text-right">{gs(item.coberturaConvenio)}</td>
                          <td className="cifra text-right font-medium">{gs(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="px-3 py-2 text-dato font-semibold uppercase text-tinta-500">
                          Paga el paciente
                        </td>
                        <td colSpan={2} />
                        <td className="cifra px-3 py-2 text-right text-base font-semibold text-tinta-900">
                          {gs(presupuesto.totalPaciente)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </Desplazable>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <NuevoPlan
        abierto={creando}
        pacienteId={paciente.id}
        alCerrar={() => setCreando(false)}
        alCrear={() => {
          setCreando(false);
          planes.recargar();
        }}
      />

      <NuevoPresupuesto
        plan={presupuestando}
        alCerrar={() => setPresupuestando(null)}
        alCrear={() => {
          setPresupuestando(null);
          presupuestos.recargar();
        }}
      />
    </div>
  );
}

function NuevoPlan({
  abierto,
  pacienteId,
  alCerrar,
  alCrear,
}: {
  abierto: boolean;
  pacienteId: string;
  alCerrar: () => void;
  alCrear: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [nombre, setNombre] = useState('Plan de tratamiento');
  const [profesionalId, setProfesionalId] = useState('');
  const [lineas, setLineas] = useState<{ prestacionId: string; fase: string; pieza: string }[]>([]);

  const tarifarios = useDatos<Tarifario[]>(abierto ? '/api/v1/tratamientos/tarifarios' : null);
  const prestaciones = useDatos<Prestacion[]>(
    tarifarios.datos?.[0] ? `/api/v1/tratamientos/prestaciones?tarifarioId=${tarifarios.datos[0].id}` : null,
    [tarifarios.datos?.[0]?.id],
  );
  const profesionales = useDatos<{ id: string; nombre: string }[]>(
    abierto ? '/api/v1/agenda/profesionales' : null,
  );

  const crear = async () => {
    const plan = await correr(() =>
      enviar<{ id: string }>('/api/v1/tratamientos/planes', {
        pacienteId,
        profesionalId,
        nombre: nombre.trim(),
      }),
    );

    if (!plan) {
      return;
    }

    for (const linea of lineas) {
      await enviar(`/api/v1/tratamientos/planes/${plan.id}/items`, {
        planId: plan.id,
        prestacionId: linea.prestacionId,
        fase: linea.fase,
        pieza: linea.pieza.trim() || null,
      }).catch(() => null);
    }

    await enviar(`/api/v1/tratamientos/planes/${plan.id}/activar`, {}).catch(() => null);

    setLineas([]);
    alCrear();
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Nuevo plan de tratamiento"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton
            variante="primario"
            ancho
            cargando={ejecutando}
            disabled={!profesionalId || lineas.length === 0}
            onClick={() => void crear()}
          >
            Crear y activar el plan
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <Campo etiqueta="Nombre del plan" value={nombre} onChange={(e) => setNombre(e.target.value)} />

        <Selector
          etiqueta="Profesional responsable"
          value={profesionalId}
          onChange={(e) => setProfesionalId(e.target.value)}
        >
          <option value="">Elegí un profesional</option>
          {(profesionales.datos ?? []).map((profesional) => (
            <option key={profesional.id} value={profesional.id}>
              {profesional.nombre}
            </option>
          ))}
        </Selector>

        <div>
          <p className="etiqueta-campo mb-2">Prestaciones del plan</p>

          {lineas.map((linea, indice) => {
            const prestacion = prestaciones.datos?.find((p) => p.id === linea.prestacionId);

            return (
              <div key={indice} className="mb-2 flex items-center gap-2 rounded-lg bg-papel-100 p-2">
                <span className="min-w-0 flex-1 truncate text-dato text-tinta-700">
                  {prestacion?.nombre ?? '—'}
                  {linea.pieza ? ` · ${linea.pieza}` : ''}
                </span>
                <Insignia>{legible(linea.fase)}</Insignia>
                <Boton
                  variante="silencioso"
                  aria-label="Quitar"
                  onClick={() => setLineas(lineas.filter((_, i) => i !== indice))}
                >
                  ✕
                </Boton>
              </div>
            );
          })}

          <AgregarLinea
            prestaciones={prestaciones.datos ?? []}
            alAgregar={(linea) => setLineas([...lineas, linea])}
          />
        </div>

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function AgregarLinea({
  prestaciones,
  alAgregar,
}: {
  prestaciones: Prestacion[];
  alAgregar: (linea: { prestacionId: string; fase: string; pieza: string }) => void;
}) {
  const [prestacionId, setPrestacionId] = useState('');
  const [fase, setFase] = useState<string>('Restauradora');
  const [pieza, setPieza] = useState('');

  const elegida = prestaciones.find((p) => p.id === prestacionId);

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-papel-300 p-3">
      <Selector
        etiqueta="Prestación"
        value={prestacionId}
        onChange={(e) => {
          setPrestacionId(e.target.value);
          const nueva = prestaciones.find((p) => p.id === e.target.value);

          if (nueva) {
            setFase(nueva.fase);
          }
        }}
      >
        <option value="">Elegí una prestación</option>
        {prestaciones.map((prestacion) => (
          <option key={prestacion.id} value={prestacion.id}>
            {prestacion.codigo} — {prestacion.nombre} ({gs(prestacion.precio)})
          </option>
        ))}
      </Selector>

      <div className="grid grid-cols-2 gap-2">
        <Selector etiqueta="Fase" value={fase} onChange={(e) => setFase(e.target.value)}>
          {FASES.map((opcion) => (
            <option key={opcion} value={opcion}>
              {legible(opcion)}
            </option>
          ))}
        </Selector>
        <Campo
          etiqueta="Pieza"
          value={pieza}
          placeholder={elegida?.requierePieza ? 'obligatoria' : 'opcional'}
          onChange={(e) => setPieza(e.target.value)}
        />
      </div>

      <Boton
        ancho
        disabled={!prestacionId}
        onClick={() => {
          alAgregar({ prestacionId, fase, pieza });
          setPrestacionId('');
          setPieza('');
        }}
      >
        Agregar al plan
      </Boton>
    </div>
  );
}

function NuevoPresupuesto({
  plan,
  alCerrar,
  alCrear,
}: {
  plan: PlanTratamiento | null;
  alCerrar: () => void;
  alCrear: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [convenioId, setConvenioId] = useState('');
  const [dias, setDias] = useState(30);
  const convenios = useDatos<ConvenioVista[]>(plan ? '/api/v1/convenios' : null);

  const crear = async () => {
    const creado = await correr(() =>
      enviar<{ id: string }>('/api/v1/presupuestos', {
        planTratamientoId: plan?.id,
        diasVigencia: dias,
        convenioId: convenioId || null,
      }),
    );

    if (creado) {
      alCrear();
    }
  };

  return (
    <Cajon
      abierto={plan != null}
      titulo="Nuevo presupuesto"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void crear()}>
            Crear presupuesto
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-dato text-tinta-500">
          Se presupuestan todas las prestaciones del plan «{plan?.nombre}» con el tarifario que
          corresponda.
        </p>

        <Selector
          etiqueta="Convenio"
          value={convenioId}
          onChange={(e) => setConvenioId(e.target.value)}
        >
          <option value="">Particular (sin convenio)</option>
          {(convenios.datos ?? []).map((convenio) => (
            <option key={convenio.id} value={convenio.id}>
              {convenio.nombre} — cubre {convenio.coberturaGeneralPorcentaje} % general
            </option>
          ))}
        </Selector>

        <Campo
          etiqueta="Días de vigencia"
          type="number"
          min={1}
          max={365}
          value={dias}
          onChange={(e) => setDias(Number(e.target.value))}
        />

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}
